import XIcon from '@elementor/icons/XIcon';
import Box from '@elementor/ui/Box';
import IconButton from '@elementor/ui/IconButton';
import Typography from '@elementor/ui/Typography';
import { styled } from '@elementor/ui/styles';
import PropTypes from 'prop-types';
import GetFreeButton from '@components/get-free-button';
import { BLOCK_TITLES } from '@constants';
import { useScannerWizardContext } from '@context/scanner-wizard-context';
import { useAnalytics } from '@hooks/use-analytics';
import GoogleAnalyticsEvent, {
	EVENT_SECTIONS,
	EVENT_TYPES,
} from '@hooks/use-analytics/google-analytics-event';
import { StyledInfoTipDescriptionBox } from '@styles/manual-fixes.styles';
import { __ } from '@utils/i18n';

const GetFreeInfoTip = ({
	videoSrc,
	posterSrc,
	title,
	description,
	onClose,
}) => {
	const { sendEvent } = useAnalytics();
	const { openedBlock } = useScannerWizardContext();

	const onGetFreeButtonClick = () => {
		const levelOne = BLOCK_TITLES[openedBlock] || BLOCK_TITLES.other;

		sendEvent(
			new GoogleAnalyticsEvent()
				.setType(EVENT_TYPES.CTA)
				.setSection(EVENT_SECTIONS.SIDEBAR)
				.setElementName('get it free button')
				.setOutcome('opening the download popup')
				.setEnglishText('Get it free')
				.setLevelOne(levelOne.toLowerCase()),
		);
	};

	return (
		<StyledGetFreeInfoTipBox>
			<StyledVideoContainer>
				<video
					src={videoSrc}
					poster={posterSrc}
					autoPlay
					loop
					muted
					playsInline
					controlsList="nodownload"
					aria-hidden="true"
				/>

				<IconButton
					onClick={onClose}
					aria-label={__('Close tooltip')}
					size="tiny"
					edge="end"
				>
					<XIcon fontSize="tiny" />
				</IconButton>
			</StyledVideoContainer>

			<StyledInfoTipDescriptionBox>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					{title}
				</Typography>

				<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
					{description}
				</Typography>

				<GetFreeButton onClick={onGetFreeButtonClick} fullWidth />
			</StyledInfoTipDescriptionBox>
		</StyledGetFreeInfoTipBox>
	);
};

GetFreeInfoTip.propTypes = {
	videoSrc: PropTypes.string.isRequired,
	posterSrc: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	description: PropTypes.string.isRequired,
	onClose: PropTypes.func,
};

const StyledGetFreeInfoTipBox = styled(Box)`
	max-width: 376px;

	white-space: normal;
`;

const StyledVideoContainer = styled(Box)`
	position: relative;

	.MuiIconButton-root {
		position: absolute;
		inset-block-start: ${({ theme }) => theme.spacing(1.25)};
		inset-inline-end: ${({ theme }) => theme.spacing(2.5)};

		padding: ${({ theme }) => theme.spacing(0.625)};

		background-color: ${({ theme }) => theme.palette.background.default};

		&:hover,
		&:focus {
			background-color: #f5f5f5;

			svg path {
				fill: ${({ theme }) => theme.palette.secondary.light};
			}
		}
	}
`;

export default GetFreeInfoTip;
